<template>
  <section class="benefits-tier-form">
    <div class="benefits-tier-fields-row">
      <div class="benefits-tier-field">
        <span class="benefits-tier-field-label">
          {{ t("benefitsCenter-tiers-panel-tierColor") }}
          <span class="benefits-required-asterisk">*</span>
        </span>
        <BenefitsColorPicker :value="value.color" @input="update('color', $event)" />
      </div>

      <div class="benefits-tier-field">
        <span class="benefits-tier-field-label">
          {{ t("benefitsCenter-tiers-panel-textColor") }}
          <span class="benefits-required-asterisk">*</span>
        </span>
        <BenefitsColorPicker :value="value.textColor" @input="update('textColor', $event)" />
      </div>
    </div>

    <div class="benefits-tier-field">
      <span class="benefits-tier-field-label">{{ t("benefitsCenter-tiers-panel-iconType") }}</span>
      <BenefitsSegmentedControl
        class="benefits-tier-icon-type"
        :value="value.iconType"
        :options="iconTypeOptions"
        :aria-label="t('benefitsCenter-tiers-panel-iconType')"
        @input="update('iconType', $event)"
      />

      <template v-if="value.iconType === 'svg'">
        <KvImageInput
          :value="svgImage"
          size="large"
          accept="image/svg+xml"
          :uploader="loadSvg"
          :show-notifications="false"
          :title="t('benefitsCenter-tiers-panel-svgUploadTitle')"
          :description="t('benefitsCenter-tiers-panel-svgUploadDescription')"
          :button-label="t('benefitsCenter-tiers-panel-svgUploadButton')"
          :delete-label="t('benefitsCenter-tiers-panel-svgUploadDelete')"
          @input="updateSvg"
        />
        <p class="benefits-tier-field-hint">{{ t("benefitsCenter-tiers-panel-svgUploadHint") }}</p>
      </template>

      <template v-else>
        <KvInput
          :value="emojiText"
          variant="outlined"
          size="large"
          :placeholder="t('benefitsCenter-tiers-panel-emojiPlaceholder')"
          @input="updateEmoji"
          @blur="emojiText = value.iconEmoji"
        />
        <p v-if="emojiText && !value.iconEmoji" class="benefits-tier-field-error">
          {{ t("benefitsCenter-tiers-panel-emojiInvalid") }}
        </p>
      </template>
    </div>

    <div class="benefits-tier-field">
      <span class="benefits-tier-field-label">{{ t("benefitsCenter-tiers-panel-preview") }}</span>
      <BenefitsTierCard :tier="previewTier" show-becoming />
    </div>
  </section>
</template>

<script>
import KvInput from "../../../components/inputs/kv-input.vue";
import KvImageInput from "../../../components/inputs/kv-image-input.vue";
import BenefitsColorPicker from "../../shared/benefits-color-picker.vue";
import BenefitsSegmentedControl from "../../shared/benefits-segmented-control.vue";
import BenefitsTierCard from "./benefits-tier-card.vue";
import { htmlToText } from "../../../helpers/html";
import { firstEmoji, readSvgFile, svgToDataUri } from "./tiers-utils";

export default {
  name: "BenefitsTierDesignForm",
  components: {
    KvInput,
    KvImageInput,
    BenefitsColorPicker,
    BenefitsSegmentedControl,
    BenefitsTierCard,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: { type: Object, required: true },
  },
  data() {
    return {
      emojiText: this.value.iconEmoji || "",
    };
  },
  computed: {
    iconTypeOptions() {
      return [
        { value: "svg", label: this.t("benefitsCenter-tiers-panel-iconSvg") },
        { value: "emoji", label: this.t("benefitsCenter-tiers-panel-iconEmoji") },
      ];
    },
    svgImage() {
      if (!this.value.iconSvg) return null;

      return {
        name: this.value.iconSvgName || this.t("benefitsCenter-tiers-panel-svgDefaultName"),
        url: svgToDataUri(this.value.iconSvg),
      };
    },
    previewTier() {
      return {
        ...this.value,
        name: this.value.name.trim() || this.t("benefitsCenter-tiers-panel-previewName"),
        description: htmlToText(this.value.description) ? this.value.description : this.t("benefitsCenter-tiers-panel-previewDescription"),
      };
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    update(field, fieldValue) {
      this.$emit("input", { ...this.value, [field]: fieldValue });
    },
    updateEmoji(text) {
      this.emojiText = text;
      this.update("iconEmoji", firstEmoji(text));
    },
    async loadSvg(file) {
      try {
        const svg = await readSvgFile(file);
        return { url: svgToDataUri(svg), svg };
      } catch (error) {
        this.$store.dispatch("notification/error", {
          text: this.t(error.message === "svg-too-large" ? "benefitsCenter-tiers-errors-iconSvgTooLarge" : "benefitsCenter-tiers-panel-svgInvalid"),
        });
        throw error;
      }
    },
    updateSvg(image) {
      this.$emit("input", {
        ...this.value,
        iconSvg: image ? image.svg || this.value.iconSvg : "",
        iconSvgName: image ? image.name : "",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tier-form';

.benefits-tier-icon-type {
  max-width: 450px;
}
</style>
